import type { SvgIcon } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FaceIcon from '@mui/icons-material/Face';
import AssignmentIcon from '@mui/icons-material/Assignment';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import MessageIcon from '@mui/icons-material/Message';
import { PermissionsUtils } from './permissionsUtils';
import { PermissionLevel, PermissionType } from '@shared/types';

interface Route<T extends unknown[] = []> {
  path: string;
  title?: string;
  icon?: typeof SvgIcon;
  permission?: { type: PermissionType; level: PermissionLevel };
  onNavigate: (...args: T) => string;
}

export class RoutePaths {
  static root = {
    path: '',
    onNavigate: () => '/',
  };

  static dashboard = {
    path: 'dashboard',
    title: 'Dashboard',
    icon: DashboardIcon,
    onNavigate: () => '/dashboard',
  };

  static applications = {
    path: 'applications',
    title: 'Applications',
    icon: AssignmentIcon,
    permission: { type: PermissionType.APPLICATIONS, level: PermissionLevel.READ },
    onNavigate: () => '/applications',
  };
  static applicationsDetails = {
    path: 'applications/:selectedApplicationId',
    onNavigate: (selectedApplicationId: string) => `/applications/${selectedApplicationId}`,
  };
  static applicationsSupplementaryDetails = {
    path: 'applications/:mainApplicationId/supplementary/:selectedApplicationId',
    onNavigate: (mainApplicationId: string, selectedApplicationId: string) =>
      `/applications/${mainApplicationId}/supplementary/${selectedApplicationId}`,
  };
  static applicationsNew = {
    path: 'applications/new',
    onNavigate: () => '/applications/new/',
  };
  static applicationsCustomerNew = {
    path: 'applications/new/:customerId',
    onNavigate: (customerId: string) => `/applications/new/${customerId}`,
  };
  static applicationsSupplementaryNew = {
    path: 'applications/:selectedApplicationId/supplementary/new',
    onNavigate: (selectedApplicationId: string) => `/applications/${selectedApplicationId}/supplementary/new`,
  };
  static applicationsEdit = {
    path: 'applications/:selectedApplicationId/edit',
    onNavigate: (selectedApplicationId: string | number) => `/applications/${selectedApplicationId}/edit`,
  };
  static applicationsSupplementaryEdit = {
    path: 'applications/:selectedApplicationId/supplementary/:supplementaryApplicationId/edit',
    onNavigate: (selectedApplicationId: string, supplementaryApplicationId: string | number) =>
      `/applications/${selectedApplicationId}/supplementary/${supplementaryApplicationId}/edit`,
  };

  static customers = {
    path: 'customers',
    title: 'Customers',
    icon: FaceIcon,
    permission: { type: PermissionType.CUSTOMER, level: PermissionLevel.READ },
    onNavigate: (queryString?: string) => `/customers${queryString || ''}`,
  };
  static customersDetails = {
    path: 'customers/:selectedCustomerId',
    onNavigate: (selectedCustomerId: string) => `/customers/${selectedCustomerId}`,
  };
  static customersAccountDetails = {
    path: 'customers/:selectedCustomerId/:selectedAccountId',
    onNavigate: (selectedCustomerId: string, selectedAccountId: string) => `/customers/${selectedCustomerId}/${selectedAccountId}`,
  };
  static customersTransactions = {
    path: 'customers/:selectedCustomerId/:selectedAccountId/transactions',
    onNavigate: (selectedCustomerId: string, selectedAccountId: string) => `/customers/${selectedCustomerId}/${selectedAccountId}/transactions`,
  };

  static manualApplications = {
    path: 'manual-applications',
    title: 'Manual Applications',
    icon: OfflineBoltIcon,
    permission: { type: PermissionType.MANUAL_APPLICATIONS, level: PermissionLevel.READ },
    onNavigate: () => '/manual-applications',
  };
  static manualApplicationsDetails = {
    path: 'manual-applications/:manualApplicationId',
    onNavigate: (manualApplicationId: string) => `/manual-applications/${manualApplicationId}`,
  };
  static manualApplicationsNew = {
    path: 'manual-applications/new',
    onNavigate: () => '/manual-applications/new',
  };

  static repository = {
    path: 'repository',
    title: 'Digital Repository',
    icon: FolderSharedIcon,
    permission: { type: PermissionType.SALES_DOCUMENTS, level: PermissionLevel.READ },
    onNavigate: () => '/repository',
  };

  static messages = {
    path: 'messages',
    title: 'Messages',
    icon: MessageIcon,
    onNavigate: () => '/messages',
  };
  static messagesThread = {
    path: 'messages/:threadId',
    onNavigate: (threadId: string) => `/messages/${threadId}`,
  };

  static login = {
    path: 'login',
    onNavigate: () => '/login',
  };
  static forgotPassword = {
    path: 'forgot-password',
    onNavigate: () => '/forgot-password',
  };
  static resetPassword = {
    path: 'reset-password/:code',
    onNavigate: (code: string) => `reset-password/${code}`,
  };

  static privateRoutes = [
    // RoutePaths.dashboard,
    RoutePaths.customers,
    RoutePaths.applications,
    RoutePaths.manualApplications,
    RoutePaths.repository,
    RoutePaths.messages,
  ] as Route[];

  static getRoutes = () =>
    RoutePaths.privateRoutes.filter((route) => !route.permission || PermissionsUtils.hasPermission(route.permission.type, route.permission.level));
}
